import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Form, Message } from 'semantic-ui-react';
import purify from 'dompurify';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getTranslation, getValue } from '../templateHelper';
import './styles.css';
import { getValidationErrorMessage } from '../../../helpers/validationHelper';

interface ITemplateRichTextEditorProps {
  id: string;
  path: string;
  label: string;
  value?: string;
  validation?: any;
  subtext?: string;
  subtextPosition?: string;
  onChange?: (path: string, html: string) => void;
}

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike', 'code'],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ script: 'sub' }, { script: 'super' }],
    ['clean']
  ]
};

// Whitelist of formats the editor accepts, for typing and for pasted content alike.
// Images and videos are not in it, so pasted markup is reduced to these. Links are
// kept because existing terms content contains them.
const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'code',
  'header',
  'blockquote',
  'code-block',
  'list',
  'indent',
  // Quill's name for subscript and superscript (<sub>/<sup>), the x₂ x² toolbar buttons.
  'script',
  'link'
];

const sanitizedHtml = (html: string): string =>
  purify.sanitize(html, {
    FORBID_CONTENTS: ['figure', 'img'],
    FORBID_TAGS: ['figure', 'img']
  });

// Content saved by the previous Draft.js editor marks underline and strikethrough as
// <ins> and <del>; Quill reads <u> and <s>.
const fromStoredHtml = (html: string = ''): string =>
  sanitizedHtml(html)
    .replace(/<(\/?)ins>/g, '<$1u>')
    .replace(/<(\/?)del>/g, '<$1s>');

// Non-breaking spaces count as whitespace: Quill exports typed spaces as &nbsp;, so a
// field holding only spaces must still read as empty for the required check.
const hasText = (html: string): boolean =>
  html
    .replace(/&nbsp;|&#160;| /g, ' ')
    .replace(/<[^>]*>/g, '')
    .trim().length > 0;

export const TemplateRichTextEditor = ({
  id,
  path,
  label,
  validation,
  value,
  subtext,
  subtextPosition = 'bottom',
  onChange
}: ITemplateRichTextEditorProps) => {
  // prettier-ignore
  const templateBuilderEx = useSelector((state: RootState) => state.templateBuilderEx);
  // prettier-ignore
  const [validationErrorMessage, setValidationErrorMessage] = useState<undefined | string | string[]>('');
  const [html, setHtml] = useState('');

  useEffect(() => {
    const stored = value || getValue(templateBuilderEx, path) || '';
    setHtml(fromStoredHtml(stored));
    setValidationErrorMessage(
      getValidationErrorMessage(validation, hasText(stored) ? stored : '')
    );
  }, [value, path]);

  const onTextChange = (
    nextHtml: string,
    _delta: unknown,
    source: 'user' | 'api' | 'silent'
  ) => {
    // Quill re-emits normalised HTML when content is loaded into it ('api' source).
    // Only the user's own edits reach the template, so stored content stays exactly
    // as saved until somebody actually changes it.
    if (source !== 'user') {
      return;
    }
    // Quill's HTML export is sanitised before it is stored, so what reaches the
    // template is bounded by the same DOMPurify rules as what is loaded into it.
    // Quill 2.0.x also writes every space as &nbsp; (quill#4509), which would stop
    // paragraphs wrapping on the public site, so those go back to plain spaces.
    const stored = hasText(nextHtml)
      ? sanitizedHtml(nextHtml).replace(/&nbsp;/g, ' ')
      : '';
    setHtml(nextHtml);
    setValidationErrorMessage(getValidationErrorMessage(validation, stored));
    if (onChange) {
      onChange(path, stored);
    }
  };

  return (
    <Form.Field id={id} required={validation.required}>
      {label && (
        <label className={`input-sub-${subtextPosition}`}>
          {getTranslation(label)}
        </label>
      )}
      {subtext && subtextPosition === 'top' && (
        <div className={`input-sub input-sub-${subtextPosition}`}>
          {getTranslation(subtext)}
        </div>
      )}
      <ReactQuill
        className="rich-text-editor-wrapper"
        theme="snow"
        value={html}
        modules={modules}
        formats={formats}
        onChange={onTextChange}
        placeholder="Enter your text here..."
      />
      <Message negative hidden={!validationErrorMessage}>
        <p>{validationErrorMessage}</p>
      </Message>
    </Form.Field>
  );
};
